import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bell } from 'lucide-react';
import { useReminders } from '@/contexts/reminders';
import AddReminderForm from '@/components/reminders/AddReminderForm';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import { usePets } from '@/contexts/pets';

interface SetReminderStepProps {
  petId: string | null;
  onReminderSet?: () => void;
}

/**
 * Third step of the onboarding tour - Set a reminder
 */
const SetReminderStep: React.FC<SetReminderStepProps> = ({ petId, onReminderSet }) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { handleAddReminder } = useReminders();
  const { pets } = usePets();

  const handleReminderSuccess = async () => {
    // No need to do anything specific on success
    // The reminder form handles the success state internally
    setIsFormVisible(false);
    toast.success('Reminder set successfully!');
    logger.info('Reminder set successfully during onboarding');
    
    // Call the onReminderSet callback if provided
    if (onReminderSet) {
      onReminderSet();
    }
  };

  return (
    <div className="flex flex-col items-center space-y-6">
      <h2 className="text-2xl font-bold text-center">Set a Reminder</h2>
      
      <p className="text-center text-gray-600">
        Almost done! You can now set a reminder for your pet's next vaccine, checkup, or anything else important.
      </p>

      <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
        <Bell className="h-10 w-10 text-yellow-500" />
      </div>

      {isFormVisible ? (
        <div className="w-full max-h-[400px] overflow-y-auto">
          <AddReminderForm 
            onSubmit={async (data) => {
              try {
                setIsProcessing(true);
                // Make sure the required fields are present
                const reminderData = {
                  ...data,
                  // If petId isn't set but we have a petId from onboarding, use it
                  petId: data.petId || (petId || 'none'),
                  title: data.title || 'Reminder',
                  date: data.date || new Date()
                };
                
                await handleAddReminder(reminderData);
                handleReminderSuccess();
              } catch (error) {
                logger.error('Error adding reminder in onboarding:', error);
                toast.error('Failed to add reminder. Please try again.');
              } finally {
                setIsProcessing(false);
              }
            }}
            onCancel={() => setIsFormVisible(false)}
            pets={pets}
          />
        </div>
      ) : (
        <div className="flex flex-col space-y-3 w-full">
          <Button 
            className="w-full" 
            onClick={() => setIsFormVisible(true)}
            disabled={isProcessing}
          >
            Set a Reminder
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => {
              if (onReminderSet) {
                onReminderSet();
              }
            }} 
            disabled={isProcessing}
            className="text-gray-500"
          >
            Skip this step
          </Button>
        </div>
      )}
    </div>
  );
};

export default SetReminderStep;
